import { LoadingModalWithLock } from "@/components/loading-modals/LoadingModalWithLock";
import { stripGqlErrorText } from "@/lib/GraphQlErrorParser";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { HeaderAgreementPickerModal } from "@/features/product-and-price/customers/common/head-agreement-picker/HeadAgreementPickerModal";
import {
  CustomerHeaderCustomersByIdFragment,
  HeadAgreementInfoRowSectionFragment,
  HeadAgreementStatusCode,
} from "@/gql/graphql";
import { useBoolean } from "@stenajs-webui/core";
import {
  SecondaryButton,
  stenaPen,
  UseResultListBannerStateResult,
} from "@stenajs-webui/elements";
import { Popover } from "@stenajs-webui/tooltip";
import { uniq } from "lodash";
import * as React from "react";
import { useCallback, useState } from "react";
import { SwitchHeadAgreementModal } from "../../list/components/SwitchHeadAgreementModal";
import { useChangeHeadAgreementStatusMutation } from "../hooks/UseChangeHeadAgreementStatusMutation";
import { DeleteHeadAgreementModal } from "./DeleteHeadAgreementModal";
import { HeadAgreementMenu } from "./HeadAgreementMenu";
import { ZIndex } from "@/lib/utils/ZIndexEnum";

interface Props {
  customer: CustomerHeaderCustomersByIdFragment | null | undefined;
  headAgreement: HeadAgreementInfoRowSectionFragment;
  resultListBanner: UseResultListBannerStateResult;
}

export const HeadAgreementActionButtons: React.FC<Props> = ({
  customer,
  headAgreement,
  resultListBanner,
}) => {
  const [switchModalIsOpen, setSwitchOpen, setSwitchClosed] = useBoolean(false);
  const [deleteModalIsOpen, setDeleteOpen, setDeleteClosed] = useBoolean(false);
  const [agreementPicker, setAgreementPicker] = useState({
    isOpen: false,
    reportType: "",
    parentCustomerId: "",
  });

  const [actionMenuOpen, openActionMenu, closeActionMenu] = useBoolean(false);
  const [loading, setLoading, setNotLoading] = useBoolean(false);

  const [changeHeadAgreementStatus] = useChangeHeadAgreementStatusMutation();
  const { setBannerState, setBannerResultWithTexts, clearBannerResult } =
    resultListBanner;

  const onChangeStatus = useCallback(
    async (statusCode: HeadAgreementStatusCode) => {
      setLoading();
      closeActionMenu();
      clearBannerResult();

      try {
        const response = await changeHeadAgreementStatus({
          variables: {
            headAgreementId: headAgreement.id,
            rowVersion: headAgreement.rowVersion,
            status: statusCode,
          },
          refetchQueries: ["HeadAgreement"],
          awaitRefetchQueries: true,
        });

        const updateResult =
          response.data?.productPrice.headAgreement.changeHeadAgreementStatus;
        if (updateResult && "errors" in updateResult) {
          setBannerState({ headerText: "Failed to update status" });
          const distinctErrorMessages = uniq(
            updateResult.errors.map((error) => error.message)
          );
          setBannerResultWithTexts(distinctErrorMessages);
        }
      } catch (e) {
        setBannerState({ headerText: "Failed to update status" });
        setBannerResultWithTexts([stripGqlErrorText(e.message)]);
      } finally {
        setNotLoading();
      }
    },
    [
      closeActionMenu,
      setLoading,
      setNotLoading,
      clearBannerResult,
      setBannerState,
      setBannerResultWithTexts,
      changeHeadAgreementStatus,
      headAgreement.id,
      headAgreement.rowVersion,
    ]
  );

  return (
    <>
      {loading && (
        <LoadingModalWithLock
          label={
            "Changing status of header agreement. Thanks for your patience."
          }
        />
      )}
      <Popover
        placement={"left"}
        disablePadding
        appendTo={document.body}
        trigger={"click"}
        zIndex={ZIndex.highest}
        renderTrigger={(props) => (
          <SecondaryButton
            label="Actions"
            leftIcon={stenaPen}
            onClick={actionMenuOpen ? closeActionMenu : openActionMenu}
            data-testid={testIdConstants.headerAgreementActionsMenu}
            {...props}
          />
        )}
      >
        <HeadAgreementMenu
          headAgreement={headAgreement}
          customer={customer}
          onOpenSwitchModal={setSwitchOpen}
          onOpenDeleteModal={setDeleteOpen}
          setAgreementPicker={setAgreementPicker}
          onRequestClose={closeActionMenu}
          onChangeStatus={onChangeStatus}
        />
      </Popover>

      {switchModalIsOpen && (
        <SwitchHeadAgreementModal
          customerId={customer?.id ?? ""}
          onRequestClose={setSwitchClosed}
          selectedHeadAgreementId={headAgreement.id}
        />
      )}
      {deleteModalIsOpen && (
        <DeleteHeadAgreementModal
          headAgreement={headAgreement}
          onRequestClose={setDeleteClosed}
        />
      )}
      {agreementPicker.isOpen && (
        <HeaderAgreementPickerModal
          onClose={() =>
            setAgreementPicker({ ...agreementPicker, isOpen: false })
          }
          parentCustomerId={agreementPicker.parentCustomerId}
          reportType={agreementPicker.reportType}
        />
      )}
    </>
  );
};
